import {
  CREATE_APPLICATION,
  UPLOAD_APPLICATION_LOGO,
  GET_DRIVER_APPLICATION,
  GET_USER_APPLICATION,
  UPDATE_APPLICATION,
  MARK_APPLICATION_INSTALLED,
  GET_APPLICATION_INSTALL_STATUS,
  RESET_APPLICATION_INSTALLED,
} from "../end-points/application";
import { GET_PROFILE_STATUS } from "../end-points/user";
import {
  getFetcher,
  postFetcher,
  formDataPostFetcher,
  formDataPatchFetcher,
  patchFetcher,
  verifyTokenPostFetcher,
} from "../fetchers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Toast } from "@/component/Shared/Toast";
import { useRouter, usePathname } from "next/navigation";
import Cookies from "js-cookie";
import { USER_ID } from "@/constants";
import { useUser } from "./user";
import { APPLICATION_ID } from "@/constants";

const useCreateApplication = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const userId = Cookies.get(USER_ID);

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [CREATE_APPLICATION],
      mutationFn: formDataPostFetcher,
      onSuccess: (data) => {
        Toast.success(data?.message);

        queryClient.setQueryData([GET_PROFILE_STATUS], (oldData: any) => ({
          ...oldData,
          user: {
            ...oldData?.user,
            driverPWA: true,
          },
        }));

        console.log("data:", data);

        localStorage.setItem(APPLICATION_ID, data?.data?._id);
      },
      onError: (error) => {
        Toast.error(error?.message);
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error,
    data,
    reset,
    trigger: (args: any) => {
      if (!userId) {
        Toast.error("Please wait while we load your user information");
        return;
      }
      return mutate({ url: `${CREATE_APPLICATION}/${userId}`, args: args });
    },
  };
};

const useUploadApplicationLogo = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  // const { data: userData, isLoading: isUserLoading } = useUser();
  const userId = Cookies.get(USER_ID);

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [UPLOAD_APPLICATION_LOGO],
      mutationFn: formDataPatchFetcher,
      onSuccess: (data) => {
        Toast.success(data?.message);

        queryClient.setQueryData([GET_PROFILE_STATUS], (oldData: any) => ({
          ...oldData,
          user: {
            ...oldData?.user,
            applicationIcon: true,
          },
        }));

        router.push(`/tasks?${APPLICATION_ID}=${data?.data?._id}`);
      },
      onError: (error) => {
        Toast.error(error?.message);
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error,
    data,
    reset,
    trigger: (args: any) => {
      if (!userId) {
        Toast.error("Please wait while we load your user information");
        return;
      }
      return mutate({
        url: `${UPLOAD_APPLICATION_LOGO}/${userId}`,
        args: args,
      });
    },
  };
};

const useDriverApplication = (applicationId: string | null) => {
  const { isPending, error, data, isError, isSuccess, refetch } = useQuery({
    queryKey: [GET_DRIVER_APPLICATION],
    queryFn: () => getFetcher(`${GET_DRIVER_APPLICATION}/${applicationId}`),
    //if set to true, API will be called the components in which it is used will be rendered
    refetchOnMount: false,
    // switching between chrome tabs
    refetchOnWindowFocus: false,
    // enabled: true,
    retry: 0, // Ensures the API is only retried once on failure
    enabled: !!applicationId,
    // enabled: false, // Set this to false to disable this query from automatically running.
  });

  return {
    data: data?.data?.application,
    isLoading: isPending,
    error,
    refetch,
  };
};

const useGetUserApplication = () => {
  const { data: userData, isLoading: isUserLoading } = useUser();
  const userId = userData?._id;

  const { isPending, error, data, isError, isSuccess, refetch } = useQuery({
    queryKey: [GET_USER_APPLICATION, userId],
    queryFn: () => getFetcher(`${GET_USER_APPLICATION}/${userId}`),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0,
    enabled: !!userId,
  });

  return {
    data: data?.data,
    isLoading: isPending || isUserLoading,
    error,
    refetch,
  };
};

const useUpdateApplication = () => {
  const queryClient = useQueryClient();
  const { data: userData, isLoading: isUserLoading } = useUser();
  const userId = userData?._id;

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [UPDATE_APPLICATION],
      mutationFn: formDataPatchFetcher,
      onSuccess: (data) => {
        Toast.success(data?.message);

        queryClient.invalidateQueries({ queryKey: [GET_USER_APPLICATION] });
      },
      onError: (error) => {
        Toast.error(error?.message);
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending || isUserLoading,
    error,
    data,
    reset,
    trigger: (args: any) => {
      if (!userId) {
        Toast.error("Please wait while we load your user information");
        return;
      }
      return mutate({ url: `${UPDATE_APPLICATION}/${userId}`, args: args });
    },
  };
};

const useMarkApplicationInstalled = () => {
  const { isPending, isError, isSuccess, mutateAsync, data, error, reset } =
    useMutation({
      mutationKey: [MARK_APPLICATION_INSTALLED],
      mutationFn: postFetcher,
      onError: () => {
        // Silent fail — install detection should not interrupt the user
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error,
    data,
    reset,
    trigger: (applicationId: string) =>
      mutateAsync({
        url: `${MARK_APPLICATION_INSTALLED}/${applicationId}`,
        args: {},
      }),
  };
};

const useGetApplicationInstallStatus = (applicationId: string | null) => {
  const { isPending, error, data, isError, isSuccess, refetch, isFetching } =
    useQuery({
      queryKey: [GET_APPLICATION_INSTALL_STATUS, applicationId],
      queryFn: () =>
        getFetcher(`${GET_APPLICATION_INSTALL_STATUS}/${applicationId}`),
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: 0,
      enabled: !!applicationId,
    });

  return {
    installed: !!data?.data?.installed,
    isLoading: isPending || isFetching,
    error,
    isError,
    isSuccess,
    refetch,
  };
};

const useResetApplicationInstalled = () => {
  const { isPending, isError, isSuccess, mutateAsync, data, error, reset } =
    useMutation({
      mutationKey: [RESET_APPLICATION_INSTALLED],
      mutationFn: postFetcher,
      onError: (error: any) => {
        Toast.error(error?.message || "Failed to reset install status");
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error,
    data,
    reset,
    trigger: (applicationId: string) =>
      mutateAsync({
        url: `${RESET_APPLICATION_INSTALLED}/${applicationId}`,
        args: {},
      }),
  };
};

export {
  useCreateApplication,
  useUploadApplicationLogo,
  useDriverApplication,
  useGetUserApplication,
  useUpdateApplication,
  useMarkApplicationInstalled,
  useGetApplicationInstallStatus,
  useResetApplicationInstalled,
};
